import { useRef, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useRequestCarrierShipmentCancellation } from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { hasHttpStatus } from "@/lib/api-error";

const CANCELLATION_REASONS = [
  { value: "customer_request", label: "Customer requested cancellation" },
  { value: "duplicate_order", label: "Duplicate order" },
  { value: "address_issue", label: "Address issue - cannot deliver" },
  { value: "shipment_canceled", label: "Shipment canceled by operator" },
  { value: "other", label: "Other" },
];

interface CancelShipmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shipment: {
    id: string;
    shipmentNumber: number;
    status: string;
  } | null;
}

export function CancelShipmentDialog({
  open,
  onOpenChange,
  shipment,
}: CancelShipmentDialogProps) {
  const requestCancellation = useRequestCarrierShipmentCancellation();
  const [reasonValue, setReasonValue] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);

  const reset = () => {
    setReasonValue("");
    setCustomReason("");
    setNotes("");
    setError(null);
  };
  const close = () => {
    if (saving) return;
    reset();
    onOpenChange(false);
  };

  const submit = async () => {
    if (!shipment || lock.current) return;
    const selectedReason =
      reasonValue === "other"
        ? customReason.trim()
        : CANCELLATION_REASONS.find((reason) => reason.value === reasonValue)
            ?.label;

    if (!selectedReason) {
      setError("Select a cancellation reason.");
      return;
    }
    const reason = notes.trim()
      ? `${selectedReason}. Notes: ${notes.trim()}`
      : selectedReason;

    lock.current = true;
    setSaving(true);
    setError(null);
    try {
      const result = await requestCancellation.mutateAsync({
        shipmentId: shipment.id,
        reason,
      });
      toast.success(
        result.immediate
          ? "Shipment cancelled."
          : "Cancellation request submitted."
      );
      reset();
      onOpenChange(false);
    } catch (caught) {
      const message = hasHttpStatus(caught, 403)
        ? "You do not have permission to cancel this shipment."
        : "Cancellation request could not be submitted.";
      setError(message);
      toast.error(message);
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };

  const isPendingPayment = shipment?.status === "PENDING_PAYMENT";

  return (
    <DetailDrawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      title={
        shipment ? `Cancel SHP-${shipment.shipmentNumber}` : "Cancel shipment"
      }
      description="Review the effect and provide a reason."
      widthClassName="sm:max-w-md"
    >
      <div className="space-y-4">
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {isPendingPayment
              ? "This unpaid shipment will be cancelled immediately."
              : "This submits a cancellation request for EZX review before final cancellation and refund handling."}
          </p>
        </div>

        <Field label="Cancellation reason" id="shipment-cancel-reason">
          <NativeSelect
            id="shipment-cancel-reason"
            value={reasonValue}
            onChange={(event) => {
              setReasonValue(event.target.value);
              setError(null);
            }}
            disabled={saving}
          >
            <option value="">Select a reason...</option>
            {CANCELLATION_REASONS.map((reason) => (
              <option key={reason.value} value={reason.value}>
                {reason.label}
              </option>
            ))}
          </NativeSelect>
        </Field>

        {reasonValue === "other" && (
          <Field label="Custom reason" id="custom-cancel-reason">
            <Textarea
              id="custom-cancel-reason"
              value={customReason}
              onChange={(event) => {
                setCustomReason(event.target.value);
                setError(null);
              }}
              disabled={saving}
            />
          </Field>
        )}

        <Field label="Notes" id="shipment-cancel-notes">
          <Textarea
            id="shipment-cancel-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={saving}
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {saving && (
          <p role="status" className="text-sm text-muted-foreground">
            Requesting shipment cancellation…
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="touch"
            className="w-full sm:w-auto"
            onClick={close}
            disabled={saving}
          >
            Keep shipment
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="touch"
            className="w-full sm:w-auto"
            onClick={() => void submit()}
            disabled={
              saving ||
              !reasonValue ||
              (reasonValue === "other" && !customReason.trim())
            }
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Requesting…" : "Cancel shipment"}
          </Button>
        </div>
      </div>
    </DetailDrawer>
  );
}

function Field({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}
